import { ReactNode } from 'react';
import s from './SessionCardMobile.module.scss';
import { StatusBadge } from '@/shared/ui/StatusBadge/StatusBadge';
import { Tooltip } from '@/shared/ui/Tooltip';
import { formatDuration } from '@/shared/lib/utils/dateTime';
import MatchIcon from '@/shared/assets/icons/match.svg';
import TrainingIcon from '@/shared/assets/icons/training.svg';
import UserIcon from '@/shared/assets/icons/user.svg';
import TimerIcon from '@/shared/assets/icons/timer.svg';
import InfoIcon from '@/shared/assets/icons/info-circle.svg';
import WalletMoneyIcon from '@/shared/assets/icons/wallet-money.svg';
import ClubInIcon from '@/shared/assets/icons/club_in.svg';
import ClubOutIcon from '@/shared/assets/icons/club_out.svg';
import ClubSelfIcon from '@/shared/assets/icons/club_self.svg';

export interface SessionCardMobileProps {
  type: 'match' | 'training';
  createdAt: string;
  subtitle?: string | null;
  title?: string | null;
  playersCount: number;
  durationSeconds: number;
  onInfoClick?: () => void;
  infoTooltip?: ReactNode;
  technocoinsSpent?: number;
  composition?: 'only_our_club' | 'our_players_in_other_session' | 'other_clubs_players';
}

export const SessionCardMobile = ({
  type,
  createdAt,
  subtitle,
  title,
  playersCount,
  durationSeconds,
  onInfoClick,
  infoTooltip,
  technocoinsSpent,
  composition,
}: SessionCardMobileProps) => {
  const dateObj = new Date(createdAt);
  const dd = !Number.isNaN(dateObj.getTime()) ? String(dateObj.getDate()).padStart(2, '0') : '';
  const mm = !Number.isNaN(dateObj.getTime()) ? String(dateObj.getMonth() + 1).padStart(2, '0') : '';
  const yy = !Number.isNaN(dateObj.getTime()) ? String(dateObj.getFullYear()).slice(-2) : '';
  const dateStr = dd && mm && yy ? `${dd}.${mm}.${yy}` : createdAt;

  const isMatch = type === 'match';
  const typeText = isMatch ? 'Матч' : 'Тренировка';
  const typeTone = isMatch ? 'orange' : 'dark';
  const TypeIcon = isMatch ? MatchIcon : TrainingIcon;

  const displayTitle = title && title !== '—' ? title : isMatch ? '—' : 'Тренировочный режим';
  const displaySubtitle = subtitle && subtitle !== '—' ? subtitle : '—';

  const infoButton = (
    <button type="button" onClick={onInfoClick} className={s.buttonIcon}>
      <InfoIcon width={20} height={20} />
    </button>
  );

  return (
    <div className={s.card}>
      <div className={s.container}>
        <div className={s.content}>
          <div className={s.subtitle}>
            <span className={s.date}>{dateStr}</span>
            <span className={s.dot} />
            <span className={s.subtitleText}>{displaySubtitle}</span>
          </div>
          <div className={s.text}>
            <span className={s.title}>{displayTitle}</span>
            {type === 'match' && composition && (
              <div className={s.description}>
                {composition === 'only_our_club' && (
                  <>
                    <ClubSelfIcon className={s.descIcon} width={16} height={16} />
                    <span className={s.descText}>Все игроки из вашего клуба</span>
                  </>
                )}
                {composition === 'other_clubs_players' && (
                  <>
                    <ClubInIcon className={s.descIcon} width={16} height={16} />
                    <span className={s.descText}>Есть игроки других клубов</span>
                  </>
                )}
                {composition === 'our_players_in_other_session' && (
                  <>
                    <ClubOutIcon className={s.descIcon} width={16} height={16} />
                    <span className={s.descText}>Игра в гостях</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {infoTooltip ? (
          <Tooltip content={infoTooltip} align="right">
            {infoButton}
          </Tooltip>
        ) : (
          infoButton
        )}
      </div>
      <div className={s.tags}>
        <StatusBadge tone={typeTone} size="s" customIcon={<TypeIcon />}>
          {typeText}
        </StatusBadge>
        {type !== 'training' && (
          <StatusBadge tone="gray" size="s" customIcon={<UserIcon />}>
            {playersCount}
          </StatusBadge>
        )}
        {technocoinsSpent !== undefined ? (
          <StatusBadge tone="gray" size="s" customIcon={<WalletMoneyIcon />}>
            {technocoinsSpent.toLocaleString('ru-RU')} Ŧ
          </StatusBadge>
        ) : (
          <StatusBadge tone="gray" size="s" customIcon={<TimerIcon />}>
            {formatDuration(durationSeconds)}
          </StatusBadge>
        )}
      </div>
    </div>
  );
};
